/**
 * Prioritised preloading for the avatar's approach intro.
 *
 * The intro is a handful of short clips (floe-api `AvatarIntroFiles`), and they
 * are not equally urgent. What decides the order is WHEN a clip is needed and
 * whether the visitor can see its absence:
 *
 *   critical  the far poster, the far idle loop and the APPROACH. The first two
 *             are what the launcher paints; the approach has to start within a
 *             frame or two of the click, so it must be fully buffered BEFORE
 *             the click — there is no time to fetch it after.
 *   next      the close idle loop (and its poster). Needed when the approach
 *             ends, a few seconds after the click. It waits for the critical tier AND
 *             for the host page's `load` event: it is the one large file here
 *             (~3.5MB), and a customer's own page must never queue behind it.
 *   filler    the gestures, then the retreat. A gesture that is not ready is
 *             simply not played, and the retreat is minutes away at the end of
 *             a call — so these load one at a time, in idle time.
 *
 * ON A CONSTRAINED CONNECTION (Save-Data, 2G) only what the launcher PAINTS is
 * loaded — the far poster and the far idle loop. The approach, the close loop
 * and every filler are speculative until someone clicks, and
 * `startup-prefetch.ts` already refuses a speculative warm on that same signal:
 * megabytes are not a download to make for a visitor who asked for less data.
 * The click then simply skips the approach, like any other not-ready clip.
 *
 * A clip that is not ready is SKIPPED, never waited for ({@link isMediaReady}).
 * The sequence degrades to what every site did before intros existed; it never
 * stalls on the network.
 *
 * HOW it loads: a detached `<video preload="auto">` / `Image`, the same warm
 * `startup-prefetch.ts` has always used. Deliberately not `fetch()`: a media
 * element is governed by the host page's `media-src`/`img-src`, which a site
 * showing the avatar already allows, while `fetch()` would add a `connect-src`
 * requirement to every customer's CSP. The elements are kept for the pageview
 * so the buffered bytes are not dropped before the visible element asks for
 * them.
 *
 * URLS MUST ALREADY BE THE `?cors=1` CACHE-KEY VARIANT (`corsCacheKeyUrl`), and
 * `crossOrigin` is assigned BEFORE `src`: a no-CORS load of that key is the
 * cache poisoning the variant exists to prevent (see AvatarVideo.tsx).
 */

export type PreloadKind = 'video' | 'image';

export interface PreloadItem {
  url: string;
  kind: PreloadKind;
}

export interface AvatarIntroPreloadPlan {
  critical: PreloadItem[];
  next: PreloadItem[];
  filler: PreloadItem[];
}

/** The subset of `AvatarMedia` the plan reads; structural, so this module imports no component. */
export interface PreloadableAvatarMedia {
  idleLoopUrl?: string;
  posterUrl?: string;
  intro?: {
    farPosterUrl: string;
    farIdleLoopUrl: string;
    approachUrl: string;
    retreatUrl?: string;
    gestureUrls: string[];
  };
}

/**
 * The load order for one avatar's media. Empty tiers when there is no intro:
 * the close loop is already warmed by `startup-prefetch.ts` on its own schedule.
 */
export function avatarIntroPreloadPlan(
  media: PreloadableAvatarMedia | null | undefined,
  toCacheKeyUrl: (url: string) => string,
): AvatarIntroPreloadPlan {
  const intro = media?.intro;
  if (!intro) return { critical: [], next: [], filler: [] };
  const video = (url: string): PreloadItem => ({ url: toCacheKeyUrl(url), kind: 'video' });
  const image = (url: string): PreloadItem => ({ url: toCacheKeyUrl(url), kind: 'image' });
  return {
    // The poster first: it is a few tens of KB and is what paints while the
    // far loop's first frame is still decoding.
    critical: [image(intro.farPosterUrl), video(intro.farIdleLoopUrl), video(intro.approachUrl)],
    next: [
      ...(media?.idleLoopUrl ? [video(media.idleLoopUrl)] : []),
      ...(media?.posterUrl ? [image(media.posterUrl)] : []),
    ],
    // Gestures before the retreat: a gesture can be due seconds after the tile
    // appears, the retreat not until a call has been started AND ended.
    filler: [...intro.gestureUrls.map(video), ...(intro.retreatUrl ? [video(intro.retreatUrl)] : [])],
  };
}

/** The leading critical items the launcher itself paints: far poster, far idle loop. */
const PAINTED_CRITICAL_ITEMS = 2;

type MediaState = 'loading' | 'ready' | 'failed';

interface RegistryEntry {
  state: MediaState;
  /** Held so the browser keeps what it buffered. */
  element: HTMLVideoElement | HTMLImageElement | null;
  settled: Promise<boolean>;
}

/** Page-scoped: a warm is a browser-cache effect, not a per-instance one. */
const registry = new Map<string, RegistryEntry>();

/** Longest one item may hold up the tier behind it. It keeps loading after this. */
export const PRELOAD_ITEM_TIMEOUT_MS = 15_000;

/**
 * Start loading one media URL (idempotent per URL) and resolve once it can play
 * through, fails, or times out. Resolves `true` only for "ready".
 */
export function warmMedia(url: string, kind: PreloadKind): Promise<boolean> {
  const existing = registry.get(url);
  if (existing) return existing.settled;

  const entry: RegistryEntry = { state: 'loading', element: null, settled: Promise.resolve(false) };
  registry.set(url, entry);
  entry.settled = new Promise<boolean>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const settle = (state: MediaState) => {
      // A late `canplaythrough` after a timeout still flips the state: the tier
      // moved on, but the clip IS ready and the sequence may use it.
      if (entry.state !== 'ready') entry.state = state;
      if (timer !== null) clearTimeout(timer);
      timer = null;
      resolve(entry.state === 'ready');
    };
    try {
      if (kind === 'image') {
        const image = new Image();
        entry.element = image;
        image.crossOrigin = 'anonymous';
        image.onload = () => settle('ready');
        image.onerror = () => settle('failed');
        image.src = url;
      } else {
        const video = document.createElement('video');
        entry.element = video;
        video.crossOrigin = 'anonymous';
        video.preload = 'auto';
        video.muted = true;
        video.addEventListener('canplaythrough', () => settle('ready'));
        video.addEventListener('error', () => settle('failed'));
        // Never attached to the document: this is a cache warm, not a render.
        video.src = url;
        try {
          video.load();
        } catch {
          /* setting src is enough on browsers that reject an explicit load() */
        }
      }
      timer = setTimeout(() => settle(entry.state === 'ready' ? 'ready' : 'loading'), PRELOAD_ITEM_TIMEOUT_MS);
    } catch {
      settle('failed');
    }
  });
  return entry.settled;
}

/** Has this URL finished buffering? The gate for "play it, or skip it". */
export function isMediaReady(url: string | undefined | null): boolean {
  return !!url && registry.get(url)?.state === 'ready';
}

export interface AvatarIntroPreloadOptions {
  /** Save-Data / 2G: load only what the launcher paints; nothing speculative. */
  constrained?: boolean;
  /**
   * Resolves once the HOST page has finished loading. Everything past the
   * critical tier waits for it. Injected so tests need no real `load` event.
   */
  whenPageLoaded?: () => Promise<void>;
  /** Yield before each filler. Injected so tests need no real idle callback. */
  whenIdle?: (run: () => void) => () => void;
  /** Called as each tier finishes, for logging and tests. */
  onTier?: (tier: keyof AvatarIntroPreloadPlan, ready: number, total: number) => void;
}

/** How long a filler may be deferred before it loads regardless. */
export const FILLER_IDLE_TIMEOUT_MS = 3_000;

function defaultWhenPageLoaded(): Promise<void> {
  return new Promise((resolve) => {
    try {
      if (typeof document === 'undefined' || document.readyState === 'complete') return resolve();
      window.addEventListener('load', () => resolve(), { once: true });
    } catch {
      resolve();
    }
  });
}

function defaultWhenIdle(run: () => void): () => void {
  try {
    const ric = (globalThis as any).requestIdleCallback;
    if (typeof ric === 'function') {
      const handle = ric(run, { timeout: FILLER_IDLE_TIMEOUT_MS });
      return () => (globalThis as any).cancelIdleCallback?.(handle);
    }
  } catch {
    /* fall through to the timer */
  }
  const timer = setTimeout(run, 200);
  return () => clearTimeout(timer);
}

/**
 * Run a plan: critical in parallel, then next in parallel, then the fillers one
 * at a time in idle time. Returns a cancel function; items already requested
 * keep loading (they are shared with every other caller), but no further item
 * is started.
 */
export function startAvatarIntroPreload(
  plan: AvatarIntroPreloadPlan,
  options: AvatarIntroPreloadOptions = {},
): () => void {
  let cancelled = false;
  let cancelIdle: (() => void) | null = null;
  // Settles the idle wait on cancel, so a cancelled run ENDS instead of
  // leaving its async frame (and the plan it closes over) pending for the page.
  let releaseIdleWait: (() => void) | null = null;
  const whenIdle = options.whenIdle ?? defaultWhenIdle;

  const runTier = async (tier: 'critical' | 'next') => {
    // Constrained: of the critical tier, only the far poster and far idle loop
    // — the two the launcher is painting — are not speculative.
    const items =
      tier === 'critical' && options.constrained ? plan.critical.slice(0, PAINTED_CRITICAL_ITEMS) : plan[tier];
    const results = await Promise.all(items.map((item) => warmMedia(item.url, item.kind)));
    options.onTier?.(tier, results.filter(Boolean).length, results.length);
  };

  const runFillers = async () => {
    let ready = 0;
    for (const item of plan.filler) {
      if (cancelled) return;
      await new Promise<void>((resolve) => {
        releaseIdleWait = resolve;
        cancelIdle = whenIdle(() => {
          cancelIdle = null;
          resolve();
        });
      });
      releaseIdleWait = null;
      if (cancelled) return;
      // ONE at a time: fillers must never compete with each other, let alone
      // with the host page, for the visitor's bandwidth.
      if (await warmMedia(item.url, item.kind)) ready += 1;
    }
    options.onTier?.('filler', ready, plan.filler.length);
  };

  void (async () => {
    try {
      await runTier('critical');
      if (cancelled || options.constrained) return;
      await (options.whenPageLoaded ?? defaultWhenPageLoaded)();
      if (cancelled) return;
      await runTier('next');
      if (cancelled) return;
      await runFillers();
    } catch {
      /* a preload that fails is a preload we do without */
    }
  })();

  return () => {
    cancelled = true;
    cancelIdle?.();
    cancelIdle = null;
    releaseIdleWait?.();
    releaseIdleWait = null;
  };
}

/**
 * Drop every warmed element and what it buffered. For SDK teardown: the
 * registry is page-scoped and would otherwise hold a dozen buffered media
 * elements for the rest of the host page's life.
 */
export function releaseWarmedMedia(): void {
  for (const entry of registry.values()) {
    const element = entry.element;
    entry.element = null;
    try {
      if (element && 'load' in element) {
        element.removeAttribute('src');
        element.load();
      } else if (element) {
        element.onload = null;
        element.onerror = null;
      }
    } catch {
      /* a detached element that will not unload is simply garbage-collected */
    }
  }
  registry.clear();
}

/** Test seam — the registry is per-page, not per-instance. */
export function resetAvatarIntroPreloadForTests(): void {
  registry.clear();
}
